'use client';

import React, {useState, useEffect, useRef} from 'react';
import {Swiper, SwiperSlide} from 'swiper/react';
import {Autoplay} from 'swiper/modules';
import {Swiper as SwiperType} from 'swiper';

// Import Swiper styles
import 'swiper/css';

interface Singer {
    id: number;
    name: string;
    imageUrl: string;
    category: string;
}

interface DesktopSingerSwiperProps {
    singers: Singer[];
    renderCard: (singer: Singer, isMobile: boolean) => React.ReactNode;
    autoPlay?: boolean;
    autoPlayInterval?: number;
    isSectionInViewPort?: boolean;
}

const DesktopSingerSwiper: React.FC<DesktopSingerSwiperProps> = ({
    singers,
    renderCard,
    autoPlay = true,
    autoPlayInterval = 1000,
    isSectionInViewPort
}) => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [hasUserInteracted, setHasUserInteracted] = useState(false);
    const swiperRef = useRef<SwiperType>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Tính toán slide bắt đầu ở giữa
    const initialSlide = Math.floor(singers.length / 2);

    // Chỉ autoplay khi user đã tương tác
    useEffect(() => {
        if (isSectionInViewPort) {
            setTimeout(() => {
                swiperRef?.current?.autoplay?.start();
            }, 500);
        } else {
            swiperRef?.current?.autoplay?.stop();
        }
    }, [isSectionInViewPort]);

    const handleBeforeInit = (swiper: SwiperType) => {
        swiperRef.current = swiper;
        swiperRef?.current?.autoplay?.stop();
    };

    const handleUserInteraction = () => {
        if (!hasUserInteracted) {
            setHasUserInteracted(true);
            setTimeout(() => {
                swiperRef?.current?.autoplay?.start();
            }, 500);
        }
    };

    const handleSlideClick = () => {
        handleUserInteraction();
    };

    const uniqueId = useRef(`desktop-swiper-${Math.random().toString(36).substr(2, 9)}`).current;

    return (
        <>
            <style>
                {`
                /* Background pulse animation */
                @keyframes backgroundPulse-${uniqueId} {
                    0%, 100% {
                        transform: scale(1) rotate(0deg);
                        opacity: 0.3;
                    }
                    50% {
                        transform: scale(1) rotate(2deg);
                        opacity: 0.6;
                    }
                }

                @keyframes particleFloat-${uniqueId} {
                    0%, 100% {
                        transform: translateY(0) rotate(0deg);
                    }
                    50% {
                        transform: translateY(-10px) rotate(180deg);
                    }
                }

                /* Swiper background effect */
                .${uniqueId} .swiper::before {
                    content: '';
                    position: absolute;
                    top: -50px;
                    left: -50px;
                    right: -50px;
                    bottom: -50px;
                    background:
                        radial-gradient(circle at 20% 20%, rgba(255, 105, 180, 0.1) 0%, transparent 50%),
                        radial-gradient(circle at 80% 80%, rgba(138, 43, 226, 0.1) 0%, transparent 50%),
                        radial-gradient(circle at 40% 60%, rgba(255, 20, 147, 0.08) 0%, transparent 50%);
                    animation: backgroundPulse-${uniqueId} 8s ease-in-out infinite;
                    z-index: -2;
                    border-radius: 20px;
                }

                .${uniqueId} .swiper-wrapper {
                    overflow: visible !important;
                }

                /* Hover effects for desktop */
                .${uniqueId} .swiper-slide:hover {
                    transform: scale(1) !important;
                    z-index: 10 !important;
                    transition: all 0.5s cubic-bezier(0.23, 1, 0.32, 1) !important;
                }

                .${uniqueId} .swiper-slide:hover > div {
                    filter: brightness(1.08) saturate(1.12) !important;
                    transform: scale(1) !important;
                    box-shadow:
                        0 20px 40px rgba(0, 0, 0, 0.22),
                        0 12px 25px rgba(255, 105, 180, 0.16),
                        0 6px 15px rgba(138, 43, 226, 0.1) !important;
                }

                /* Active slide effects */
                .${uniqueId} .swiper-slide-active > div {
                    filter: brightness(1.1) saturate(1.15) !important;
                    transition: all 0.5s cubic-bezier(0.23, 1, 0.32, 1);
                    transform: scale(1) !important;
                }

                .${uniqueId} .swiper-slide-active:hover > div {
                    box-shadow:
                        0 35px 70px rgba(0, 0, 0, 0.3),
                        0 20px 40px rgba(255, 105, 180, 0.22),
                        0 12px 25px rgba(138, 43, 226, 0.18) !important;
                }

                /* Glow effect for slides */
                .${uniqueId} .swiper-slide::before {
                    content: '';
                    position: absolute;
                    top: -2px;
                    left: -2px;
                    right: -2px;
                    bottom: -2px;
                    background: linear-gradient(45deg,
                        rgba(255, 105, 180, 0.15),
                        rgba(255, 20, 147, 0.15),
                        rgba(138, 43, 226, 0.15));
                    border-radius: 18px;
                    z-index: -1;
                    opacity: 0;
                    transition: opacity 0.5s ease;
                }

                .${uniqueId} .swiper-slide:hover::before {
                    opacity: 1;
                }

                /* 3D click effect */
                .${uniqueId} .swiper-slide:active {
                    transform: scale(1) rotateY(8deg) rotateX(3deg) !important;
                    transition: all 0.15s ease !important;
                }

                /* Non-active slides */
                .${uniqueId} .swiper-slide:not(.swiper-slide-active) {
                    filter: grayscale(0.2) blur(0.3px);
                    opacity: 0.7;
                    transition: all 0.7s ease;
                }

                .${uniqueId} .swiper-slide:not(.swiper-slide-active):hover {
                    filter: grayscale(0) blur(0);
                    opacity: 1;
                }

                /* Ensure active slide is always bright */
                .${uniqueId} .swiper-slide-active {
                    filter: none !important;
                    opacity: 1 !important;
                }

                /* Particle effect */
                .${uniqueId} .swiper-slide::after {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background-image:
                        radial-gradient(circle at 20% 30%, rgba(255, 255, 255, 0.1) 1px, transparent 1px),
                        radial-gradient(circle at 80% 70%, rgba(255, 105, 180, 0.1) 1px, transparent 1px),
                        radial-gradient(circle at 40% 60%, rgba(138, 43, 226, 0.1) 1px, transparent 1px);
                    background-size: 30px 30px, 40px 40px, 25px 25px;
                    opacity: 0;
                    transition: opacity 0.7s ease;
                    animation: particleFloat-${uniqueId} 4s ease-in-out infinite;
                    z-index: 2;
                    pointer-events: none;
                }

                .${uniqueId} .swiper-slide:hover::after {
                    opacity: 1;
                }

                /* Cursor styles */
                .${uniqueId} .swiper:hover {
                    cursor: grab;
                }

                .${uniqueId} .swiper:active {
                    cursor: grabbing;
                }

                /* Responsive desktop */
                @media (min-width: 1280px) {
                    .${uniqueId} .swiper-slide {
                        width: 380px !important;
                    }

                    .${uniqueId} .swiper-slide:hover {
                        transform: scale(1) rotateY(5deg) !important;
                    }
                }
                `}
            </style>

            <div className={`relative w-full overflow-hidden ${uniqueId}`} ref={containerRef}>
                <Swiper
                    modules={[Autoplay]}
                    loop={true}
                    onBeforeInit={handleBeforeInit}
                    onInit={handleBeforeInit}
                    autoplay={
                        autoPlay
                            ? {
                                  delay: autoPlayInterval,
                                  disableOnInteraction: false,
                              }
                            : false
                    }
                    grabCursor={true}
                    slidesPerView={3}
                    centeredSlides={true}
                    spaceBetween={30}
                    initialSlide={initialSlide}
                    style={{
                        width: '100%',
                        maxWidth: '1200px',
                        overflow: 'visible',
                        padding: '30px 0 50px 0',
                        position: 'relative',
                    }}
                    //onClick={handleSlideClick}
                    onSlideChange={(swiper) => {
                        setActiveIndex(swiper.realIndex);
                        //handleUserInteraction();
                    }}
                    //onTouchStart={handleUserInteraction}
                >
                    {singers.map((singer) => (
                        <SwiperSlide
                            key={singer.id}
                            style={{
                                width: '320px',
                                height: 'auto',
                                cursor: 'pointer',
                                transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                                overflow: 'visible',
                                position: 'relative',
                            }}
                        >
                            <div
                                style={{
                                    transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                                    borderRadius: '16px',
                                    overflow: 'hidden',
                                    position: 'relative',
                                    filter: 'brightness(1.02) saturate(1.05)',
                                    transform: 'scale(0.8)',
                                }}
                            >
                                {renderCard(singer, false)}
                            </div>
                        </SwiperSlide>
                    ))}
                </Swiper>
            </div>
        </>
    );
};

export default DesktopSingerSwiper;
